import { Dispatch, SetStateAction, useState } from "react";
import { UseFormReturn } from "react-hook-form";

import { useTranslation } from "../../core/translation.core";
import usePageBtnNavRange from "../../hooks/usePageNavBtnRange.hook";
import ChevronLeftIcon from "../../icons/chevronLeft.icon";
import ChevronRightIcon from "../../icons/chevronRight.icon";
import { MediaFilter } from "../../types/media";
import { inputCss } from "../reuseables/native.comp";

interface PageNavProps {
  page: number;
  size: number;
  total: number;
  methods: UseFormReturn<MediaFilter>;
}

export default function MediaListPageNav({ page, size, total, methods }: PageNavProps) {
  const [navigatePage, setNavigatePage] = useState<number>(page);
  const { mobile, desktop, pageCount } = usePageBtnNavRange({
    total,
    size,
    currentPage: page,
    mobileBtnsNum: 3,
    desktopBtnsNum: 10,
  });

  const handleClick = (page: number) => {
    setNavigatePage(page);
    methods.setValue("page", page);
  };

  return (
    <div className="mt-2 py-4 xl:grid grid-cols-[1fr_auto_1fr]">
      <div></div>
      <div className="flex gap-2 justify-center items-center ">
        <NavBtn disabled={page === 1} onClick={() => handleClick(page - 1)}>
          <ChevronLeftIcon className="w-5 h-5" />
        </NavBtn>

        {/* mobile nav List */}
        <NavBtnList range={mobile} page={page} screen="mobile" onClick={handleClick} />
        {/* Desktop nav List */}
        <NavBtnList range={desktop} page={page} screen="desktop" onClick={handleClick} />

        <NavBtn disabled={page >= pageCount} onClick={() => handleClick(page + 1)}>
          <ChevronRightIcon className="w-5 h-5" />
        </NavBtn>
      </div>
      <NavInputForm
        pageCount={pageCount}
        currentPage={page}
        navigatePage={navigatePage}
        setNavigatePage={setNavigatePage}
        methods={methods}
      />
    </div>
  );
}

interface NavBtnListProps {
  range: number[];
  page: number;
  screen: "mobile" | "desktop";
  onClick: (page: number) => void;
}

const NavBtnList = ({ range, page, screen, onClick }: NavBtnListProps) => {
  const style = screen === "mobile" ? "flex md:hidden" : "hidden md:flex  gap-2";

  return (
    <div className={style}>
      {range.map((i) => (
        <NavBtn key={i} isActive={page === i} onClick={() => onClick(i)}>
          {i}
        </NavBtn>
      ))}
    </div>
  );
};

interface NavBtnProps {
  children: React.ReactNode;
  isActive?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}
const NavBtn = ({ children, isActive, disabled = false, onClick }: NavBtnProps) => {
  if (disabled) return null;

  let className = "w-8 h-8 flex justify-center items-center rounded-full border border-gray-300 ";

  if (isActive) {
    className += "bg-gray-500 text-white ";
  }

  if (disabled) {
    className += "bg-gray-200 text-white ";
  }

  return (
    <button type="button" onClick={onClick} className={className}>
      {children}
    </button>
  );
};

interface NavInputForm {
  pageCount: number;
  currentPage: number;
  navigatePage: number;
  setNavigatePage: Dispatch<SetStateAction<number>>;
  methods: UseFormReturn<MediaFilter>;
}
const NavInputForm = ({ pageCount, currentPage, navigatePage, setNavigatePage, methods }) => {
  const { t, msg } = useTranslation();

  const handleSubmit = () => {
    if (navigatePage === currentPage || navigatePage < 1) return;
    methods.setValue("page", navigatePage);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSubmit();
    }
  };

  return (
    <div className="text-base place-self-end flex justify-end items-center">
      <label className="mx-2">
        {t(msg.product.page)}
        <input
          className={`${inputCss} !w-16 `}
          min={1}
          max={pageCount}
          type="number"
          value={navigatePage}
          onChange={(e) => {
            setNavigatePage(e.target.valueAsNumber);
          }}
          onKeyDown={handleKeyDown}
          step={1}
        />
      </label>
      / {pageCount}
      <input
        type="button"
        onClick={handleSubmit}
        value={t(msg.product.go)}
        className="bg-primary border-primary border-2 text-white px-3 ml-2 py-1 cursor-pointer hover:bg-primary-dark hover:border-primary-dark"
      />
    </div>
  );
};
